'use client';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { FaArrowLeftLong } from 'react-icons/fa6';
import { usePathname } from 'next/navigation';
import { RiDownload2Fill } from 'react-icons/ri';
import { TbShoppingCart } from 'react-icons/tb';
import AddToCartModal from 'src/components/shared/components/add-to-cart-button/add-to-cart-modal';

import cn from '@/lib/clsxm';
import { APIErrorResponseGenerator } from '@/lib/helper';
import handleGA4Event, { eventTracker } from '@/lib/service-workers';

import { SIZE_ENUM } from '@/components/@base/@helpers/types';
import Button from '@/components/@base/button';
import Container from '@/components/@base/container';
import { showToast } from '@/components/@base/toast/show-toast';
import { Text } from '@/components/@base/typography';
import XImage from '@/components/@base/x-image';
import Auth from '@/components/app/auth/components';
import { usePostAddToCart } from '@/components/app/cart/services/hooks';
import { useGetProfile } from '@/components/layout/services/hooks';
import Props from '@/components/shared/components/add-to-cart-button/type';
import { useGetFreeDownloadLink } from '@/components/shared/services/hooks';

import { useAddonsStore } from '@/store/addons.store';
import useCommonModalStore from '@/store/common-modal-store';
import { usePrerequisitesStore } from '@/store/prerequisites.store';

import {
  DATA_CY_ADD_TO_CARD_BUTTON,
  DATA_CY_ADD_TO_CARD_POP_UP_HEAD,
  DATA_CY_MESSAGE,
} from '@/constant/cypress';
import ICON_SIZE, { ICON_COLOR } from '@/constant/icon-size-color';
import imagesList from '@/constant/images-list';
import localization, { pageLevelLocalization } from '@/constant/localization';
import { GET_CART } from '@/constant/query-key';

const { product: productLocalization } = pageLevelLocalization;

export default function AddToCartButton({
  productId,
  demoId,
  children,
  size,
  disabled,
  slug,
  className,
  isFree = false,
  ...props
}: Props) {
  const { setShow } = useCommonModalStore();
  const queryClient = useQueryClient();
  const { profile } = useGetProfile();
  const { mutate, isPending } = usePostAddToCart();
  const { addons, setAddons } = useAddonsStore();
  const selectedAddOns = Object.keys(addons).filter((item) => addons[item].selected);
  const { prerequisites } = usePrerequisitesStore();
  const unselectedPrerequisites = Object.keys(prerequisites).filter(
    (id) => !prerequisites[id].selected,
  );
  const { isAuthenticated } = useGetProfile();
  const {
    data: downloadLink,
    refetch: refetchDownloadLink,
    isLoading: isGetDownloadLinkPending,
  } = useGetFreeDownloadLink(productId);

  const handleFreeDownload = () => {
    const fetchDownloadLink = async () => {
      refetchDownloadLink()
        .then((response) => {
          if (response.data) {
            const link = document.createElement('a');
            link.href = response.data;
            link.setAttribute('download', ''); // Optional: you can specify a default filename here
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          } else {
            showToast({ message: 'Download link is not available', type: 'error' });
          }
        })
        .catch((error) => {
          //TODO: handle error appropriately
          console.log(error);
        });
    };

    if (isAuthenticated) {
      fetchDownloadLink();
    } else {
      handleAuthModal();
    }
  };
  const handleAddToCart = () => {
    mutate(
      {
        id: productId,
        slug: selectedAddOns,
        demo_id: demoId,
        prerequisite: unselectedPrerequisites,
      },
      {
        onError: (e) => {
          if (e instanceof AxiosError) showToast(APIErrorResponseGenerator(e));
        },
        onSuccess: (res) => {
          if (res.appliedCodeSteppedDiscount) {
            showToast({ message: res.appliedCodeSteppedDiscount, type: 'default' });
          }
          queryClient.setQueryData([GET_CART], res);
          eventTracker(profile?.mobile, 'add_to_basket', new Date().toISOString(), {
            productId,
          });
          const product = res?.products?.find((item) => item?.id === productId);
          handleGA4Event(
            'add_to_cart',
            [
              {
                item_id: productId,
                item_name: product?.title,
                price: product?.price,
                index: 0,
                quantity: 1,
                affiliation: product?.store?.slug,
                discount: product?.discount,
              },
            ],
            {
              currency: 'USD',
              value: res?.total,
            },
          );
          const steppedDiscount = res?.steppedDiscount;
          const cartRes = res;
          handleOpenModal(steppedDiscount, cartRes);
        },
      },
    );
  };

  const handleOpenModal = (steppedDiscount?: any, cartRes?: any) => {
    setShow(true, {
      DialogPanelProps: {
        className: 'flex flex-col rounded-xl p-0! max-w-[600px] max-h-[90vh] overflow-hidden!',
      },
      DialogProps: {
        className: 'z-[2147483647]',
        onClose: () => {
          setAddons({});
          setShow(false);
        },
      },
      Head() {
        return (
          <Container data-cy={DATA_CY_ADD_TO_CARD_POP_UP_HEAD} className='shrink-0 p-1.5'>
            <Container
              center
              className='flex-col rounded-lg bg-[linear-gradient(180deg,_#81cf3312_0%,_#ffffff00_43.89%)] py-[26px]'
            >
              <XImage
                src={imagesList.icons.addToCartIcon}
                alt={productLocalization.productAddedToCart}
              />
              <Text bold size={SIZE_ENUM.LG} className='text-[#424244]'>
                {productLocalization.productAddedToCart}
              </Text>
            </Container>
          </Container>
        );
      },
      Body() {
        return (
          <AddToCartModal
            slug={slug}
            productId={productId}
            demoId={demoId}
            steppedDiscounts={steppedDiscount}
            cart={cartRes}
            onClose={handleCloseModal}
          />
        );
      },
    });
  };
  const handleCloseModal = () => {
    setShow(false);
    setAddons({}); //clear the selected addons when closing the modal
  };
  const handleAuthModal = () => {
    setShow(true, {
      DialogPanelProps: {
        className: 'rounded-xl overflow-hidden shadow-none!  p-0! bg-transparent! ',
      },
      Body() {
        return <Auth />;
      },
    });
  };

  return isFree ? (
    <Button
      size={size}
      isLoading={isGetDownloadLinkPending}
      disabled={disabled}
      onClick={handleFreeDownload}
      className={cn('gap-2.5 hover:bg-[#EB8800]', className)}
      Icon={<RiDownload2Fill color={ICON_COLOR.white} size={ICON_SIZE.sm} />}
    >
      {localization.freeDownload}
    </Button>
  ) : (
    <Button
      size={size}
      isLoading={isPending}
      disabled={disabled}
      data-cy={DATA_CY_ADD_TO_CARD_BUTTON}
      onClick={handleAddToCart}
      className={cn('gap-2.5 hover:bg-[#EB8800]', className)}
      {...props}
    >
      {children}
    </Button>
  );
}
